import { CognitoIdentityClient } from "@aws-sdk/client-cognito-identity";
import { applyPhotoOrder } from "../../photoOrder";
import {
  DynamoDBClient,
  ScanCommand,
  GetItemCommand,
  UpdateItemCommand,
} from "@aws-sdk/client-dynamodb";
import { S3Client, ListObjectsCommand } from "@aws-sdk/client-s3";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";
import { decodePayload } from "../utils/cipher";
import { kyivDateKey } from "../utils/format";
import {
  REGION as MEDIA_REGION,
  S3_PHOTOS_BUCKET,
  photoUrl,
  thumbUrl,
} from "../../media";

export const REGION = MEDIA_REGION;
export const IDENTITY_POOL_ID =
  "eu-central-1:713c28fd-d5fa-4e35-a9de-b89640b12b65";

const TABLE_OBJECTS = "objects_v3";
const TABLE_QUIZ = "daily_quiz";
const TABLE_CONFIG = "site_config";

/**
 * Fields the public site is allowed to read. Owner name and phone number are
 * deliberately absent — the matching IAM policy on the Cognito
 * *unauthenticated* role must enforce the same list with a
 * `dynamodb:Attributes` condition, otherwise this is only cosmetic.
 * See AWS_SETUP.md.
 */
const PUBLIC_PROJECTION_BASE =
  "id, cost, currency, dateFrom, dateEdit, floor, furniture, heating, " +
  "info, #rg, rooms, #st, street, technology";

const PUBLIC_PROJECTION = PUBLIC_PROJECTION_BASE + ", photoOrder";

/**
 * Whether the guest role is allowed to read `photoOrder`.
 *
 * The guest policy pins the exact attribute list with a
 * `ForAllValues:StringEquals dynamodb:Attributes` condition, so asking for an
 * attribute it does not list is not a partial answer — the whole request is
 * denied, and with it every listing on the site. Adding `photoOrder` to the
 * projection without adding it to the policy therefore takes the public site
 * down completely.
 *
 * Rather than depend on the two being deployed in step, the first denial
 * retries without `photoOrder` and remembers. The site keeps working; photos
 * just fall back to alphabetical order until the policy catches up.
 */
var canReadPhotoOrder = true;

function projectionExpression() {
  return canReadPhotoOrder ? PUBLIC_PROJECTION : PUBLIC_PROJECTION_BASE;
}

function isProjectionDenied(err) {
  if (!canReadPhotoOrder || !err) return false;
  const name = err.name || "";
  return (
    name === "AccessDeniedException" ||
    name === "AccessDenied" ||
    name === "ValidationException"
  );
}

/** Run a query; if the guest role cannot see photoOrder, drop it and retry. */
async function sendWithProjection(build) {
  try {
    return await publicDdb.send(build(projectionExpression()));
  } catch (err) {
    if (!isProjectionDenied(err)) throw err;
    console.warn(
      "Гостьова роль не має доступу до photoOrder — порядок фото буде " +
        "алфавітним. Додайте photoOrder у dynamodb:Attributes гостьової " +
        "політики (див. AWS_SETUP.md).",
      err
    );
    canReadPhotoOrder = false;
    return publicDdb.send(build(PUBLIC_PROJECTION_BASE));
  }
}

// `state` and `region` are DynamoDB reserved words — they must be aliased.
const PUBLIC_PROJECTION_NAMES = { "#st": "state", "#rg": "region" };

/* -------------------------------------------------------------------------- */
/* Anonymous AWS clients                                                       */
/* -------------------------------------------------------------------------- */

const anonymousCredentials = fromCognitoIdentityPool({
  client: new CognitoIdentityClient({ region: REGION }),
  identityPoolId: IDENTITY_POOL_ID,
});

export const publicDdb = new DynamoDBClient({
  region: REGION,
  credentials: anonymousCredentials,
});

export const publicS3 = new S3Client({
  region: REGION,
  credentials: anonymousCredentials,
});

/* -------------------------------------------------------------------------- */
/* Marshalling                                                                 */
/* -------------------------------------------------------------------------- */

function fromAttr(attr) {
  if (attr === undefined || attr === null) return undefined;
  if ("S" in attr) return attr.S;
  if ("N" in attr) return Number(attr.N);
  if ("BOOL" in attr) return attr.BOOL;
  if ("NULL" in attr) return null;
  if ("L" in attr) return attr.L.map(fromAttr);
  if ("M" in attr) return fromItem(attr.M);
  if ("SS" in attr) return attr.SS;
  if ("NS" in attr) return attr.NS.map(Number);
  return undefined;
}

function fromItem(item) {
  if (!item) return null;
  var out = {};
  Object.keys(item).forEach(function (key) {
    out[key] = fromAttr(item[key]);
  });
  return out;
}

/** Belt-and-braces: never let contact fields reach the public UI. */
function sanitize(obj) {
  if (!obj) return obj;
  delete obj.personName;
  delete obj.phoneNumber;
  delete obj.aboutClient;
  return obj;
}

/* -------------------------------------------------------------------------- */
/* Site config                                                                 */
/* -------------------------------------------------------------------------- */

var configCache = null;
var configCachedAt = 0;
var configInflight = null;
const CONFIG_TTL_MS = 60 * 1000;
const CONFIG_ID = "site";

/** What we fall back to whenever the flag cannot be read. See below. */
const CONFIG_DEFAULT = { maintenance: false };

/**
 * Reads the single `site_config` row that decides whether the public site shows
 * apartments at all. See MAINTENANCE.md — the flag is flipped by hand in the
 * DynamoDB console; nothing in the app writes it.
 *
 * FAILS OPEN, and that is the important part: a missing table, a missing IAM
 * permission on the unauthenticated role, a throttle or a network blip all
 * resolve to "not in maintenance". The site is only ever hidden because the
 * database explicitly said so — never because a read failed.
 */
export function loadSiteConfig() {
  var now = Date.now();

  if (configCache && now - configCachedAt < CONFIG_TTL_MS) {
    return Promise.resolve(configCache);
  }
  if (configInflight) return configInflight;

  configInflight = publicDdb
    .send(
      new GetItemCommand({
        TableName: TABLE_CONFIG,
        Key: { id: { S: CONFIG_ID } },
      })
    )
    .then(function (data) {
      var item = fromItem(data.Item) || {};
      return { maintenance: item.maintenance === true };
    })
    .catch(function (err) {
      // Fail open — but say so out loud. A silent fallback here is very hard to
      // diagnose from the outside: the flag reads `true` in the console, the
      // site keeps showing apartments, and nothing anywhere mentions that the
      // read was denied. Missing `dynamodb:GetItem` on site_config for the
      // Cognito unauth role is the usual cause.
      console.warn("LeoCity: не вдалося прочитати site_config, режим обслуговування вважається вимкненим.", err);
      return CONFIG_DEFAULT;
    })
    .then(function (value) {
      configCache = value;
      configCachedAt = Date.now();
      configInflight = null;
      return value;
    });

  return configInflight;
}

/* -------------------------------------------------------------------------- */
/* Listings                                                                    */
/* -------------------------------------------------------------------------- */

var listingsCache = null;
var listingsCachedAt = 0;
var listingsInflight = null;
const LISTINGS_TTL_MS = 3 * 60 * 1000;
const MAX_LISTINGS = 1200;

/**
 * Loads every published listing, paging through the DynamoDB scan.
 * Results are cached in memory so moving between pages is instant.
 */
export function loadListings(options) {
  var force = options && options.force;
  var now = Date.now();

  if (!force && listingsCache && now - listingsCachedAt < LISTINGS_TTL_MS) {
    return Promise.resolve(listingsCache);
  }
  if (listingsInflight) return listingsInflight;

  listingsInflight = (async function () {
    var items = [];
    var startKey = undefined;

    do {
      var data = await sendWithProjection(function (projection) {
        return new ScanCommand({
          TableName: TABLE_OBJECTS,
          ProjectionExpression: projection,
          ExpressionAttributeNames: PUBLIC_PROJECTION_NAMES,
          Limit: 200,
          ExclusiveStartKey: startKey,
        });
      });
      (data.Items || []).forEach(function (raw) {
        var obj = sanitize(fromItem(raw));
        if (obj && obj.id) items.push(obj);
      });
      startKey = data.LastEvaluatedKey;
    } while (startKey && items.length < MAX_LISTINGS);

    items.sort(function (a, b) {
      return (b.dateEdit || b.dateFrom || 0) - (a.dateEdit || a.dateFrom || 0);
    });

    listingsCache = items;
    listingsCachedAt = Date.now();
    listingsInflight = null;
    return items;
  })();

  listingsInflight.catch(function () {
    listingsInflight = null;
  });

  return listingsInflight;
}

export async function loadListing(id) {
  if (!id) return null;

  if (listingsCache) {
    var hit = listingsCache.filter(function (i) {
      return i.id === id;
    })[0];
    if (hit) return hit;
  }

  var data = await sendWithProjection(function (projection) {
    return new GetItemCommand({
      TableName: TABLE_OBJECTS,
      Key: { id: { S: id } },
      ProjectionExpression: projection,
      ExpressionAttributeNames: PUBLIC_PROJECTION_NAMES,
    });
  });
  if (!data.Item) return null;
  return sanitize(fromItem(data.Item));
}

/* -------------------------------------------------------------------------- */
/* Photos                                                                      */
/* -------------------------------------------------------------------------- */

var photoCache = {};
var photoInflight = {};

export function loadPhotos(objectId, order) {
  if (!objectId) return Promise.resolve([]);
  if (photoCache[objectId]) return Promise.resolve(photoCache[objectId]);
  if (photoInflight[objectId]) return photoInflight[objectId];

  var promise = publicS3
    .send(
      new ListObjectsCommand({
        Bucket: S3_PHOTOS_BUCKET,
        Prefix: encodeURIComponent(objectId) + "/",
      })
    )
    .then(function (data) {
      var photos = (data.Contents || [])
        .filter(function (photo) {
          return photo.Key && !/\/$/.test(photo.Key);
        })
        .map(function (photo) {
          return {
            // `url` is the original; `thumbUrl` is the ~640px copy used by
            // cards and strips. The thumbnail may not exist yet for older
            // listings, so every consumer falls back to `url` on error.
            url: photoUrl(photo.Key),
            thumbUrl: thumbUrl(photo.Key),
            name: photo.Key.split("/").pop(),
            key: photo.Key,
          };
        });
      // The realtor's chosen order, saved on the listing itself; without it
      // the cover would be whichever filename sorts first.
      var sorted = applyPhotoOrder(photos, order);
      photoCache[objectId] = sorted;
      delete photoInflight[objectId];
      return sorted;
    })
    .catch(function (err) {
      console.warn("Не вдалося завантажити фото для " + objectId, err);
      delete photoInflight[objectId];
      photoCache[objectId] = [];
      return [];
    });

  photoInflight[objectId] = promise;
  return promise;
}

/* -------------------------------------------------------------------------- */
/* Daily crossword                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Reads today's crossword. The puzzle body is stored obfuscated so the answers
 * are not sitting in plain sight in the network tab (see utils/cipher.js).
 */
export async function loadDailyQuiz(dateKey) {
  var key = dateKey || kyivDateKey();

  var data = await publicDdb.send(
    new GetItemCommand({
      TableName: TABLE_QUIZ,
      Key: { id: { S: key } },
    })
  );
  if (!data.Item) return null;

  var raw = fromItem(data.Item);
  var puzzle = null;
  try {
    puzzle = decodePayload(raw.payload);
  } catch (e) {
    console.error("Пошкоджений кросворд для " + key, e);
    return null;
  }
  if (!puzzle || !puzzle.grid || !puzzle.entries) return null;

  return {
    id: raw.id,
    title: raw.title || "Кросворд дня",
    topic: raw.topic || "",
    rows: puzzle.grid.length,
    cols: puzzle.grid[0] ? puzzle.grid[0].length : 0,
    grid: puzzle.grid,
    entries: puzzle.entries,
    solved: !!raw.solved,
    solvedBy: raw.solvedBy || "",
    solvedAt: raw.solvedAt || 0,
    solveCount: raw.solveCount || 0,
    generatedAt: raw.generatedAt || 0,
  };
}

/**
 * Records a solve. The first solver of the day is stored separately from the
 * running counter, so "розгадано" survives later solvers.
 */
export async function recordQuizSolved(dateKey, nickname, seconds) {
  var key = dateKey || kyivDateKey();
  var name = (nickname || "Анонім").toString().slice(0, 24);
  var now = Date.now();

  // First solver of the day — only lands if nobody has claimed it yet.
  try {
    var data = await publicDdb.send(
      new UpdateItemCommand({
        TableName: TABLE_QUIZ,
        Key: { id: { S: key } },
        UpdateExpression:
          "SET solved = :true, solvedBy = :name, solvedAt = :now, " +
          "solveSeconds = :sec ADD solveCount :one",
        ConditionExpression:
          "attribute_exists(id) AND (attribute_not_exists(solved) OR solved = :false)",
        ExpressionAttributeValues: {
          ":true": { BOOL: true },
          ":false": { BOOL: false },
          ":name": { S: name },
          ":now": { N: String(now) },
          ":sec": { N: String(Math.max(0, Math.round(seconds || 0))) },
          ":one": { N: "1" },
        },
        ReturnValues: "ALL_NEW",
      })
    );
    return { first: true, item: fromItem(data.Attributes) };
  } catch (err) {
    if (err && err.name !== "ConditionalCheckFailedException") {
      console.warn("Не вдалося зберегти результат кросворда", err);
      return { first: false, item: null, error: err };
    }
  }

  // Somebody was faster — just bump the counter.
  try {
    var bumped = await publicDdb.send(
      new UpdateItemCommand({
        TableName: TABLE_QUIZ,
        Key: { id: { S: key } },
        UpdateExpression: "ADD solveCount :one",
        ConditionExpression: "attribute_exists(id)",
        ExpressionAttributeValues: { ":one": { N: "1" } },
        ReturnValues: "ALL_NEW",
      })
    );
    return { first: false, item: fromItem(bumped.Attributes) };
  } catch (err2) {
    console.warn("Не вдалося оновити лічильник кросворда", err2);
    return { first: false, item: null, error: err2 };
  }
}
